import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Card, StatTile } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InvoiceClient, type InvoiceDTO } from "../../api/invoice";
import { clientLabel } from "../../api/pricing";
import { RANK_SCOPES, rankScopeLabel, rankText, scopedRank, type ProductDTO, type WarehouseDTO } from "../../api/product";
import { ShopifyClient, shopifyAdminProductUrl } from "../../api/shopify";
import { STATUS_VARIANT, TYPE_VARIANT } from "./badges";
import { isoDate } from "../../api/reporting";

/**
 * The product at a glance, read-only: who it is, what it is worth, where its
 * stock sits, how it sells, what it last shipped on, whether it is on Shopify.
 * Everything here is already loaded with the product or cached by another tab
 * (invoices, the Shopify listings); the tab adds no writes and no state.
 */
export function OverviewTab({ draft, warehouses }: { draft: ProductDTO; warehouses: WarehouseDTO[] }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const invoices = useMemo(() => new InvoiceClient(http), [http]);
  const shopify = useMemo(() => new ShopifyClient(http), [http]);
  const sku = draft.metadata?.sku ?? "";

  const period = useMemo(() => {
    const now = new Date();
    return { from: `${now.getFullYear() - 2}-01-01`, to: isoDate(now) };
  }, []);
  // Same key as the Invoices tab: one fetch serves both.
  const lastInvoices = useQuery({
    queryKey: ["invoice", "by-part", sku, period.from],
    queryFn: () => invoices.byPeriodAndPart(sku, period),
    enabled: sku !== "",
    staleTime: 5 * 60_000,
  });
  const listings = useQuery({ queryKey: ["shopify", "listed"], queryFn: () => shopify.listed(), staleTime: 5 * 60_000 });
  const store = useQuery({ queryKey: ["shopify", "store"], queryFn: () => shopify.store(), staleTime: 60 * 60_000, retry: false });

  const inventory = draft.kpi_inventory;
  const sales = draft.kpi_sales;
  const warehouseRows = warehouses
    .map((warehouse) => ({ warehouse, entry: inventory?.warehouse_quantities?.[String(warehouse.id)] ?? null }))
    .filter((row) => row.entry !== null || dispatchesFrom(draft, row.warehouse.id));
  const listing = draft.variation_group == null ? undefined : (listings.data ?? []).find((l) => l.variation_group === draft.variation_group);

  const white = (draft.images ?? []).find((image) => image.image_type === "WHITE_BACKGROUND") ?? draft.images?.[0];
  const recent = [...(lastInvoices.data ?? [])].sort((a, b) => (b.date ?? "").localeCompare(a.date ?? "")).slice(0, 5);

  return (
    <div className="flex flex-col gap-4">
      {/* ── identity ─────────────────────────────────────────────────────── */}
      <Card className="p-5">
        <div className="flex flex-wrap gap-5">
          {white?.thumbnail?.url || white?.image?.url ? (
            <img
              src={white.thumbnail?.url ?? white.image?.url ?? ""}
              alt=""
              className="h-36 w-36 shrink-0 rounded border border-line object-contain bg-white"
            />
          ) : (
            <div className="flex h-36 w-36 shrink-0 items-center justify-center rounded border border-line text-[12px] text-ink-faint">
              {t("product.overview.no-image", "No image")}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[17px] font-semibold text-ink">{draft.description_en?.product_name || t("product.overview.unnamed", "Unnamed product")}</h2>
              <Badge variant={TYPE_VARIANT[draft.type] ?? "neutral"} size="sm">{draft.type}</Badge>
              <Badge variant={STATUS_VARIANT[draft.status] ?? "neutral"} size="sm">{draft.status}</Badge>
            </div>
            {draft.description_fr?.product_name && (
              <p className="text-[13px] text-ink-muted">{draft.description_fr.product_name}</p>
            )}
            <dl className="mt-3 grid gap-x-8 gap-y-2 text-[13px] sm:grid-cols-2 xl:grid-cols-3">
              <Fact label={t("column.sku", "SKU")} value={sku} mono />
              <Fact label={t("product.metadata.upc", "UPC")} value={draft.metadata?.upc} mono />
              <Fact label={t("column.category", "Category")} value={draft.category?.name} />
              <Fact label={t("column.collection", "Collection")} value={(draft.collections ?? []).map((c) => c.name).join(", ")} />
              <Fact label={t("column.designer", "Designer")} value={draft.designer?.name} />
              <Fact
                label={t("product.variations.group", "Variation group")}
                value={draft.variation_group == null ? null : `${draft.variation_group} · ${(draft.variations ?? []).map((v) => v.value).join(" / ") || ""}`}
              />
              <Fact label={t("product.metadata.hs", "HS code")} value={draft.metadata?.harmonized_code} mono />
              <Fact label={t("product.metadata.origin", "Origin")} value={draft.metadata?.country_of_origin} />
              <Fact label={t("product.overview.created", "Created")} value={draft.created_at?.slice(0, 10)} />
            </dl>
            {(draft.tags ?? []).length > 0 && (
              <p className="mt-3 flex flex-wrap gap-1">
                {(draft.tags ?? []).map((tag) => (
                  <Badge key={tag.id} variant="neutral" size="sm">{tag.name}</Badge>
                ))}
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* ── figures ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {/* The first tile: the global rank and each market's, one line each,
            with that scope's weekly velocity. */}
        <Card className="p-4">
          <p className="text-[11px] font-medium uppercase tracking-wider text-ink-faint">{t("product.kpi.ranking", "Ranking")}</p>
          <dl className="mt-2 grid grid-cols-[auto_auto_1fr] items-baseline gap-x-2 gap-y-1">
            {RANK_SCOPES.map((scope) => {
              const { rank, velocity } = scopedRank(draft, scope);
              return (
                <Fragment key={scope}>
                  <dt className="text-[11px] text-ink-muted">{rankScopeLabel(scope, t)}</dt>
                  <dd className="tabular text-[15px] font-semibold leading-tight text-ink">{rankText(rank, draft.type, t) ?? ""}</dd>
                  <dd className="tabular whitespace-nowrap text-right text-[11px] text-ink-faint">
                    {t("product.kpi.velocity-short", "{{velocity}}/wk", { velocity: num(velocity) })}
                  </dd>
                </Fragment>
              );
            })}
          </dl>
        </Card>
        <StatTile
          label={t("product.overview.ytd-sales-ca", "YTD sales CA")}
          value={money(sales?.ytd_sales_cad)}
          note={t("product.overview.units", "{{count}} units", { count: sales?.ytd_units_cad ?? 0 })}
        />
        <StatTile
          label={t("product.overview.ytd-sales-us", "YTD sales US")}
          value={money(sales?.ytd_sales_usd)}
          note={t("product.overview.units", "{{count}} units", { count: sales?.ytd_units_usd ?? 0 })}
        />
        <StatTile
          label={t("product.overview.last-sale", "Last sale")}
          value={sales?.last_sale_date ?? ""}
          note={sales?.last_sale_aging_days != null ? t("product.overview.days-ago", "{{count}} days ago", { count: sales.last_sale_aging_days }) : undefined}
        />
        <StatTile label={t("column.cogs", "COGS")} value={money(draft.pricing?.cogs)} />
        <StatTile label={t("product.kpi.in-production", "In production")} value={num(inventory?.production_quantity)} />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        {/* ── inventory ─────────────────────────────────────────────────── */}
        <Card className="p-5">
          <h3 className="mb-4 text-[13px] font-semibold text-ink">{t("product.overview.inventory", "Inventory")}</h3>
          <table className="w-full text-[13px]">
            <thead className="text-left text-ink-faint">
              <tr>
                <th className="pb-2 font-medium">{t("column.warehouse", "Warehouse")}</th>
                <th className="pb-2 text-right font-medium">{t("product.overview.on-hand", "On hand")}</th>
                <th className="pb-2 text-right font-medium">{t("product.overview.reserved", "Reserved")}</th>
                <th className="pb-2 text-right font-medium">{t("product.overview.in-order", "In order")}</th>
                <th className="pb-2 text-right font-medium">{t("product.kpi.available", "Available")}</th>
                <th className="pb-2 text-right font-medium">{t("product.overview.dispatch", "Dispatches to")}</th>
              </tr>
            </thead>
            <tbody>
              {warehouseRows.map(({ warehouse, entry }) => (
                <tr key={warehouse.id} className="border-t border-line">
                  <td className="py-1.5 font-medium">{warehouse.code}</td>
                  <td className="py-1.5 text-right tabular">{num(entry?.quantity)}</td>
                  <td className="py-1.5 text-right tabular">{num(entry?.reserved_quantity)}</td>
                  <td className="py-1.5 text-right tabular">{num(entry?.in_order_quantity)}</td>
                  <td className="py-1.5 text-right tabular font-semibold">{entry ? num(entry.quantity - entry.reserved_quantity) : ""}</td>
                  <td className="py-1.5 text-right">{marketsServedBy(draft, warehouse.id).join(", ") || ""}</td>
                </tr>
              ))}
              {warehouseRows.length === 0 && (
                <tr className="border-t border-line">
                  <td colSpan={6} className="py-2 text-ink-faint">{t("product.overview.no-inventory", "No stock recorded")}</td>
                </tr>
              )}
            </tbody>
            {inventory && (
              <tfoot className="border-t border-line text-ink-muted">
                <tr>
                  <td className="pt-2">{t("product.overview.total", "Total")}</td>
                  <td className="pt-2 text-right tabular">{num(inventory.quantity)}</td>
                  <td className="pt-2 text-right tabular">{num(inventory.reserved_quantity)}</td>
                  <td className="pt-2 text-right tabular">{num(inventory.in_order_quantity)}</td>
                  <td className="pt-2 text-right tabular font-semibold">{num(inventory.quantity - inventory.reserved_quantity)}</td>
                  <td className="pt-2" />
                </tr>
              </tfoot>
            )}
          </table>
          {inventory && (
            <p className="mt-2 text-[12px] text-ink-faint">
              {[
                inventory.inventory_value ? `${t("product.overview.value", "Inventory value (COGS × available)")} ${money(inventory.inventory_value)}` : null,
                inventory.planned_quantity ? t("product.overview.planned", "Planned: {{count}}", { count: inventory.planned_quantity }) : null,
                inventory.safety_stock_quantity
                  ? t("product.overview.safety-stock", "Safety stock: {{count}} (pipeline planning floor, not deducted)", { count: inventory.safety_stock_quantity })
                  : null,
                inventory.first_stock_date ? `${t("product.overview.first-stock", "first stocked")} ${inventory.first_stock_date}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
        </Card>

        {/* ── sales ─────────────────────────────────────────────────────── */}
        <Card className="p-5">
          <h3 className="mb-4 text-[13px] font-semibold text-ink">{t("product.overview.sales", "Sales")}</h3>
          <table className="w-full text-[13px]">
            <thead className="text-left text-ink-faint">
              <tr>
                <th className="pb-2 font-medium">{t("product.overview.period", "Period")}</th>
                <th className="pb-2 text-right font-medium">{t("product.overview.units-ca", "Units CA")}</th>
                <th className="pb-2 text-right font-medium">{t("product.overview.units-us", "Units US")}</th>
                <th className="pb-2 text-right font-medium">{t("product.overview.sales-ca", "Sales CAD")}</th>
                <th className="pb-2 text-right font-medium">{t("product.overview.sales-us", "Sales USD")}</th>
              </tr>
            </thead>
            <tbody>
              {salesRows(sales, t).map((row) => (
                <tr key={row.label} className="border-t border-line">
                  <td className="py-1.5">{row.label}</td>
                  <td className="py-1.5 text-right tabular">{num(row.unitsCa)}</td>
                  <td className="py-1.5 text-right tabular">{num(row.unitsUs)}</td>
                  <td className="py-1.5 text-right tabular">{money(row.salesCa)}</td>
                  <td className="py-1.5 text-right tabular">{money(row.salesUs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        {/* ── last invoices ─────────────────────────────────────────────── */}
        <Card className="p-5">
          <h3 className="mb-4 text-[13px] font-semibold text-ink">{t("product.overview.last-invoices", "Last invoices")}</h3>
          {lastInvoices.isLoading ? (
            <p className="text-[13px] text-ink-faint">{t("loading", "Loading…")}</p>
          ) : recent.length === 0 ? (
            <p className="text-[13px] text-ink-faint">{t("product.overview.no-invoices", "No invoice in the last two years")}</p>
          ) : (
            <table className="w-full text-[13px]">
              <thead className="text-left text-ink-faint">
                <tr>
                  <th className="pb-2 font-medium">{t("column.date", "Date")}</th>
                  <th className="pb-2 font-medium">{t("column.invoice", "Invoice")}</th>
                  <th className="pb-2 font-medium">{t("column.client", "Client")}</th>
                  <th className="pb-2 font-medium">{t("column.order", "Order")}</th>
                  <th className="pb-2 text-right font-medium">{t("column.subtotal", "Subtotal")}</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((invoice: InvoiceDTO) => (
                  <tr key={invoice.id} className="border-t border-line">
                    <td className="py-1.5 tabular">{invoice.date ?? ""}</td>
                    <td className="py-1.5 tabular">{invoice.no ?? ""}</td>
                    <td className="py-1.5 truncate">{invoice.client ? clientLabel(invoice.client) : ""}</td>
                    <td className="py-1.5 tabular">{invoice.order_no ?? ""}</td>
                    <td className="py-1.5 text-right tabular">{money(invoice.subtotal)} {invoice.currency ?? ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* ── channels ──────────────────────────────────────────────────── */}
        <Card className="p-5">
          <h3 className="mb-4 text-[13px] font-semibold text-ink">{t("product.overview.channels", "Channels")}</h3>
          <dl className="grid gap-x-8 gap-y-2 text-[13px] sm:grid-cols-2">
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-ink-faint">Shopify</dt>
              <dd className="mt-0.5 flex items-center gap-2">
                {draft.shopify_excluded || listing?.excluded ? (
                  <Badge variant="warning" size="sm">{t("shopify.status.excluded", "Excluded")}</Badge>
                ) : listing?.product_gid ? (
                  <Badge variant="success" size="sm">{t("shopify.status.listed", "Listed")}</Badge>
                ) : (
                  <Badge variant="neutral" size="sm">{t("shopify.status.not-listed", "Not on Shopify")}</Badge>
                )}
                {listing?.product_gid && store.data && (
                  <a href={shopifyAdminProductUrl(store.data.admin_url, listing.product_gid)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline">
                    {t("shopify.link.admin", "Shopify admin")} <ExternalLink className="size-3" />
                  </a>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-ink-faint">{t("product.tab.inventory-rules", "Inventory rules")}</dt>
              <dd className="mt-0.5">
                {(draft.inventory_rules ?? []).filter((r) => r.is_listed).length} {t("product.overview.listed", "channel(s) listed")}
                {(draft.inventory_rules ?? []).some((r) => r.is_listed && r.mode === "BLOCKED") ? ` · ${(draft.inventory_rules ?? []).filter((r) => r.is_listed && r.mode === "BLOCKED").length} ${t("product.overview.blocked", "blocked")}` : ""}
                {(draft.inventory_rules ?? []).some((r) => r.is_listed && r.mode === "FLOOR") ? ` · ${(draft.inventory_rules ?? []).filter((r) => r.is_listed && r.mode === "FLOOR").length} ${t("product.overview.floored", "floored")}` : ""}
              </dd>
            </div>
          </dl>
        </Card>
      </div>
    </div>
  );
}

function Fact({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] uppercase tracking-wider text-ink-faint">{label}</dt>
      <dd className={`mt-0.5 truncate text-ink ${mono ? "tabular" : ""}`}>{value || ""}</dd>
    </div>
  );
}

function salesRows(sales: ProductDTO["kpi_sales"], t: (key: string, fallback: string) => string) {
  const s = sales;
  return [
    { label: t("product.overview.period.4w", "4 weeks"), unitsCa: s?.four_weeks_units_cad, unitsUs: s?.four_weeks_units_usd, salesCa: s?.four_weeks_sales_cad, salesUs: s?.four_weeks_sales_usd },
    { label: t("product.overview.period.12w", "12 weeks"), unitsCa: s?.twelve_weeks_units_cad, unitsUs: s?.twelve_weeks_units_usd, salesCa: s?.twelve_weeks_sales_cad, salesUs: s?.twelve_weeks_sales_usd },
    { label: t("product.overview.period.52w", "52 weeks"), unitsCa: s?.fifty_two_weeks_units_cad, unitsUs: s?.fifty_two_weeks_units_usd, salesCa: s?.fifty_two_weeks_sales_cad, salesUs: s?.fifty_two_weeks_sales_usd },
    { label: t("product.overview.period.ytd", "Year to date"), unitsCa: s?.ytd_units_cad, unitsUs: s?.ytd_units_usd, salesCa: s?.ytd_sales_cad, salesUs: s?.ytd_sales_usd },
    { label: t("product.overview.period.ytd-1", "Last year to date"), unitsCa: s?.ytd_minus_1_units_cad, unitsUs: s?.ytd_minus_1_units_usd, salesCa: s?.ytd_minus_1_sales_cad, salesUs: s?.ytd_minus_1_sales_usd },
  ];
}

function dispatchesFrom(draft: ProductDTO, warehouseId: number): boolean {
  return marketsServedBy(draft, warehouseId).length > 0;
}

function marketsServedBy(draft: ProductDTO, warehouseId: number): string[] {
  const markets: string[] = [];
  for (const [market, ids] of Object.entries(draft.source_warehouses ?? {})) {
    if ((ids ?? []).includes(warehouseId)) markets.push(market);
  }
  return markets;
}

function num(value: number | null | undefined): string {
  return value == null ? "" : value.toLocaleString();
}

function money(value: number | null | undefined): string {
  return value == null ? "" : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
